/**
 * Some types that need to be accessed globally
 */

import type { IProductAllocation, ISelectedProduct } from './product';
import type { PublicationType } from './publication';
import type { RegistrationStepMarker } from './registration';

export interface IOrderDraft {
  company_name: string;
  workload_min: number | null;
  workload_max: number | null;
  workload_permanent: number | null;
  workload_fulltime: number | null;
  title: string;
  workplace_location: string | null;
  selected_products: ISelectedProduct[];
  startdate?: string;
  apply_url?: string;
  enddate?: string;
  budget_gross_total?: number;
  budget_currency?: string;
  comment?: string;
  rte_blocks: any[];
  bot_program_id?: string | null;
  application_receivers: string[];
}

export interface Order {
  id?: number | null;
  application_receivers?: string[];
  creation_source: 'direct_url' | 'file_import' | 'web_import' | 'custom';
  provider?: string;
  provider_id?: number;
  provider_job_id?: string | null;
  ejm_job_id?: number;
  provider_job_group_id?: number;
  provider_company_id?: number;
  provider_name?: string;
  provider_active?: boolean;
  customer_id?: number;
  company_id?: number;
  last_provider_sync?: number;
  last_request_id?: number;
  campaign_prefix?: string;
  assigned_user_id?: number;
  assigned_user_name?: string;
  is_draft?: boolean;
  title?: string;
  directlink?: string;
  apply_url?: string;
  language?: string;
  lead_order_status?: 'to_be_confirmed' | 'confirmed' | 'running' | 'finished' | 'waiting_to_finish' | 'unknown' | null;
  startdate: string;
  enddate: string;
  segment_ids?: [];
  workload_min?: number;
  workload_max?: number;
  workload_permanent?: boolean;
  workload_fulltime?: boolean;
  industry?: string;
  department?: string;
  company_name?: string;
  company_street?: string;
  company_zip?: number;
  company_city?: string;
  company_country?: string;
  company_homepage?: string;
  workplace_location?: string;
  workplace_city?: string;
  workplace_country?: string;
  workplace_zip?: string;
  budget_gross_total?: number;
  budget_currency?: string;
  stats_total_cost_euros?: number;
  stats_budget_total_euros?: number;
  stats_clicks_total?: number;
  stats_impressions_total?: number;
  comment?: string;
  workplace_address?: string;
  created_at?: string;
  modified_at?: string;
  segment_ids_are_overridden?: boolean;
  segment_ids_original?: [];
  directlink_preview?: string;
  duration?: {
    days: number;
    days_left: number;
    progress: number;
  };
  company?: { data: Company };
  preferred_plattforms?: string[];
  selected_products?: ISelectedProduct[];
  stats_cache: { last_build: string | null; data: { [statDate: string]: OrderStatsItem } | [] };
  stats_cost_per_click?: number;
  creator?: Creator;
  publications?: { data: PublicationType[] };
  job_products?: { data: IProductAllocation[] };
  rte_blocks?: any[];
  bot_program_id?: string | null;
  bot_id?: number | null;
  step?: string;
  external_confirmation?: boolean;
  confirmed_by_customer?: boolean;
  initial_job_posting?: boolean;
}
export interface Creator {
  data: CreatorData;
  type: string;
  name: string;
}

export interface CreatorData {
  id: number;
  customer_id: number;
  is_active: boolean;
  is_admin: boolean;
  is_hidden: boolean;
  email: string;
  firstname: string;
  lastname: string;
  locale: string;
  created_at: string;
  modified_at: string;
  last_access: any;
  created_by: any;
  modified_by: any;
}

export interface OrderStatsItem {
  clicks: number;
  impressions: number;
}

export interface OrderMedia {
  companyLogo: ImageObject[];
  images: ImageObject[];
}

export interface OrderPreview {
  campaignId?: string;
  description: string;
  height: number;
  key: string;
  name: string;
  template: string;
  width: number;
}

export interface ResponseOrderImage {
  id: number;
  sequence: number;
  type: 'company_logo' | 'image';
  created_at: string;
  updated_at: string;
  image: ImageObject;
}

/** CrawledImageStatus enum values from the backend */
export const CrawledImageStatus = {
  NeverRequested: 0,
  Pending: 1,
  Ready: 2,
  Error: 3,
} as const;

export type CrawledImageStatusValue = (typeof CrawledImageStatus)[keyof typeof CrawledImageStatus];

export interface CrawledImage {
  image_id: number;
  resource_id: number;
  hash: string;
  src_url: string;
  proxy_url: string;
  width: number | null;
  height: number | null;
  alt: string | null;
  score: number | null;
}

export interface CrawledImagesResponse {
  data: {
    status: CrawledImageStatusValue;
    status_message: string;
    images: CrawledImage[];
  };
}

export interface ImageObject {
  id: number;
  status: number;
  hash: string;
  extension: string;
  width: number;
  height: number;
  bits: number;
  channels?: any[];
  size: number;
  class: number;
  description: null | string;
  inscription: string;
  source: string;
  colors_popular: number[];
  colors_representive: number[];
  color_background?: any;
  focus_x: null | number;
  focus_y: null | number;
  proxy_url: string | string;
  media_object_id: null | number;
  media_stock_purchase_id: null | number;
  created_at: string;
  modified_at: string;
  proxy: {
    url_raw: string;
    url_preview: string;
    url_thumbnail: string;
  };
  type: 'company_logo' | 'image' | 'crawled' | null;
  // class_string: 'banner' | 'company_logo' | 'image' | null;string
  class_string: string;
  blob?: File; // For frontend use
  selected?: boolean; // For frontend use
  resource_id?: number; // For crawled images: bn-manager resource id, needed by save payload
}

export interface VideoObject {
  id: number;
  status: number;
  hash: string;
  extension: 'mp4';
  file_name: string;
  filesize_bytes: number;
  main_thumbnail_image_id: number;
  codec: string;
  container: string;
  height: number;
  width: number;
  length_milliseconds: number;
  frames_per_second: number;
  total_frames: number;
  bitrate: number;
  description: null | string;
  source: string;
  media_object_id: number;
  created_at: string;
  modified_at: string;
  proxy: {
    url_thumbnail: string;
  };
}

export type CustomerAccount = User;
export interface User {
  id: number;
  customer_id: number;
  is_active: boolean;
  is_admin: boolean;
  is_hidden: boolean;
  email: string;
  firstname: string | null;
  lastname: string | null;
  job_title: string | null;
  locale: string;
  created_at: string;
  modified_at: string;
  last_access: string;
  created_by: string | null;
  modified_by: string | null;
  customer?: { data: Customer | null };
  /** the mobile, masked by the backend (full E.164 for admins) */
  phone_number: string | null;
  /** when the mobile was confirmed, if ever */
  phone_verified_at: string | null;
  /** how far an in-progress self-registration has got; null when finished or pre-wizard. Lets the guard route without an extra request. */
  registration_step: RegistrationStepMarker | null;
}

/** how a verification code is delivered */
export type PhoneCodeChannel = 'sms' | 'call';

/** asks for a code; `phone` is absent when the number on file should be used */
export interface PhoneSendRequest {
  phone?: string;
  channel: PhoneCodeChannel;
}

/** `phone` is the number the code was sent to, when it differs from the one on file */
export interface PhoneVerifyRequest {
  code: string;
  phone?: string;
}

export interface PhoneSendResult {
  resendAfterSeconds: number;
  maskedNumber?: string;
}

export interface PhoneVerifiedResponse {
  phoneNumber: string;
  phoneVerifiedAt: string;
}

export interface Customer {
  id: number;
  name: string;
  prefix: string;
  currency: string;
  deleted: boolean;
  has_unaccapted_eula: boolean;
  limited_access_start: string | null;
  limited_access_end: string | null;
  /** set for a self-registered LEAD customer; null for one Prospective onboarded manually. */
  self_company_id: number | null;
  /** true once required feature defaults resolved; false if one still blocks order creation; null if not self-registered or not yet processed. */
  registration_defaults_applied: boolean | null;
}

export interface PromptsuiteApp {
  id: number;
  llmor_app_id: number;
  name: string;
  description: string;
  last_sync: string;
  created_at: string;
  modified_at: string;
  configuration: { [key: string]: any };
  translations: { [key: string]: { [key: string]: string } };
}

export interface Eula {
  id: number;
  name: string;
  text: string;
  created_at: string;
  modified_at: string | null;
}

export interface EulaAssignment {
  id: number;
  customer_id: number;
  accepted: boolean;
  accepted_by: number | null;
  accepted_at: string | null;
  created_at: string;
  modified_at: string | null;
  eula?: { data: Eula };
}
export interface Company {
  firstname?: string;
  id: number;
  customer_id: number;
  provider_id: string;
  name: string;
  address_street: string;
  address_city: string;
  address_zip: string;
  address_country_iso: string;
  address_country: string;
  homepage: string;
  logo_image_id?: number;
  default_dir: any;
  logo_image?: LogoImage;
  logo_uploaded_by: { type: 'user' | 'customer_account'; data: User | null };
  images?: ImageObject[];
  parent?: Company | null;
}

export interface LogoImage {
  data?: LogoImageData;
}

export interface LogoImageData {
  id: number;
  status: number;
  hash: string;
  extension: string;
  width: number;
  height: number;
  bits: number;
  channels: any;
  size: number;
  class: number;
  description: string;
  inscription: string;
  source: string;
  colors_popular: number[];
  colors_representive: number[];
  color_background: any;
  focus_x: any;
  focus_y: any;
  proxy_url: string;
  media_object_id: number;
  media_stock_purchase_id: any;
  created_at: string;
  modified_at: string;
  proxy: Proxy;
  class_string: string;
}

export interface Proxy {
  url_raw: string;
  url_preview: string;
  url_thumbnail: string;
}

export interface OrderLimits {
  minTotalBudget: number;
  minDailyBudget: number;
  minDurationDays: number;
  calculatedStartDate: string;
  calculatedEndDate: string;
}

export interface CampaignProvider {
  id: string;
  provider: string;
  provider_logo: string;
  provider_name: string;
  provider_status: string;
  groups: FieldGroup[];
  previews: OrderPreview[];
  can_be_confirmed: boolean;
  confirmed: boolean;
  can_be_pushed: boolean;
  finished: number;
  push_status: number;
  active: number;
  values_valid: boolean;
}

export interface FieldGroup {
  key: string;
  name: string;
  properties: FieldGroupProperty[];
}

export interface FieldGroupProperty {
  key: string;
  label?: string;
  type: string;
  options?: any;
  exclusive_option?: string | null;
  /** Guidance shown under the input. Plain text, never markup. */
  description?: string;
  /** Whether the customer must set it before the campaign can be submitted. */
  required?: boolean;
}

/**
 * One place, as the geocoding endpoint returns it and as a manual `location` field stores it.
 *
 * Only `formatted_address`, `lat` and `lon` are always present: a city-level match carries no street
 * number and a country-level match no postal code. `place_id` identifies a place across lookups.
 */
export interface GeocodedLocation {
  place_id: string | null;
  formatted_address: string;
  lat: number;
  lon: number;
  street: string | null;
  street_number: string | null;
  zip: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  country_code: string | null;
}

export interface AdPlacement {
  ad_id: number | null;
  media_type: string;
  placement_group: string;
  ad_label: string;
  video_id: number | null;
  image_id: number;
}

export interface Ad {
  id: number | null;
  primary_texts: string[];
  individual_placement: boolean;
  ad_placements: AdPlacement[];
}

export interface TextAssetOptions {
  max_lines?: number;
}
